import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Crown, X, TrendingUp } from 'lucide-react';

export const UpgradeModal = ({ isOpen, onClose, feature, currentLimit, tier = 'free' }) => {
  const navigate = useNavigate();

  if (!isOpen) return null;

  const featureNames = {
    websites: 'Websites',
    crm_accounts: 'CRM Accounts',
    digiStrategy: 'Digi Strategy Generations',
    digiPresentor: 'Digi Presentor Generations',
    digiEditor: 'Digi Editor Downloads'
  };

  const upgradeFeatures = {
    pro_lite: [
      '10 Websites with Custom Domains',
      'Unlimited CRM Accounts',
      'Unlimited Digi Strategy',
      'Unlimited Digi Presentor',
      '20 Digi Editor images/day'
    ],
    pro_max: [
      'Unlimited Everything',
      'No Watermarks',
      'Priority Support',
      'Advanced Analytics',
      'API Access'
    ]
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full shadow-2xl relative animate-in fade-in zoom-in duration-300">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
        >
          <X className="w-6 h-6" />
        </button>

        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-8 rounded-t-lg text-center">
          <Crown className="w-16 h-16 mx-auto mb-4" />
          <h2 className="text-3xl font-bold mb-2">Upgrade to Unlock More</h2>
          <p className="text-lg opacity-90">
            You've reached your {featureNames[feature] || 'feature'} limit
          </p>
        </div>

        {/* Content */}
        <div className="p-8">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <p className="text-yellow-800 font-semibold">
              {tier === 'free' && '🚀 Free Plan: '}
              {currentLimit && `You've used ${currentLimit.current}/${currentLimit.max} ${featureNames[feature]}`}
            </p>
            {currentLimit?.resetAt && (
              <p className="text-yellow-700 text-sm mt-1">
                Resets on {new Date(currentLimit.resetAt).toLocaleDateString()}
              </p>
            )}
          </div>

          {/* Features */}
          <div className="mb-8">
            <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-blue-600" />
              With Pro, you get:
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="border-2 border-blue-100 rounded-lg p-4 hover:border-blue-300 transition-colors">
                <h4 className="font-bold text-blue-600 mb-3">Pro Lite - $10/mo</h4>
                <ul className="space-y-2 text-sm">
                  {upgradeFeatures.pro_lite.map((f, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-green-500 mt-0.5">✓</span>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="border-2 border-purple-100 rounded-lg p-4 hover:border-purple-300 transition-colors">
                <h4 className="font-bold text-purple-600 mb-3">Pro Max - $20/mo</h4>
                <ul className="space-y-2 text-sm">
                  {upgradeFeatures.pro_max.map((f, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-green-500 mt-0.5">✓</span>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-4">
            <Button
              onClick={() => navigate('/pricing')}
              className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white py-6 text-lg"
            >
              <Crown className="w-5 h-5 mr-2" />
              View Pricing Plans
            </Button>
            <Button
              onClick={onClose}
              variant="outline"
              className="px-8 py-6"
            >
              Maybe Later
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
